export type NotificationType =
  | 'E_VERIFY_EXPIRATION_60_DAYS'
  | 'E_VERIFY_EXPIRATION_30_DAYS'
  | 'E_VERIFY_EXPIRATION_7_DAYS'
  | 'E_VERIFY_EXPIRED'
  | 'ESIGN_SIGNING_REQUEST'
  | 'ESIGN_REMINDER'
  | 'ESIGN_COMPLETED'
  | 'TIME_OFF_REQUEST_APPROVED'
  | 'TIME_OFF_REQUEST_DENIED'
  // STFFLG-4014: added to or removed from a client's order-access team (metadata companyId /
  // companyName). Sent since 4014; missing here until STFFLG-4184.
  | 'TEAM_ROLE_ASSIGNED'
  | 'TEAM_ROLE_REMOVED'
  | JobOrderInvitationNotificationType

/**
 * STFFLG-4184 (Job Order Access Part 2): a cross-branch job order invitation, mspplatform
 * notification-type.enum.ts. Sent only while mspplatform's `orderAccessInvites` flag is on.
 * - RECEIVED: to the invitee (sent, and each re-invite);
 * - ACCEPTED / DECLINED: to the host and the effective co-host (and the inviter when neither);
 * - WITHDRAWN: to the invitee, when the invitation or their helper access was taken back
 *   (`metadata.reason`). It has no action: the order is no longer theirs to open;
 * - EXPIRED: to the invitee (its action opens Pending Orders) and to the host side (the order).
 * Access ending normally at the order's cutoff notifies nobody.
 */
export type JobOrderInvitationNotificationType =
  | 'JOB_ORDER_INVITE_RECEIVED'
  | 'JOB_ORDER_INVITE_ACCEPTED'
  | 'JOB_ORDER_INVITE_DECLINED'
  | 'JOB_ORDER_INVITE_WITHDRAWN'
  | 'JOB_ORDER_INVITE_EXPIRED'

/** Why a JOB_ORDER_INVITE_WITHDRAWN notification was sent (mspplatform JobOrderInvitationNoticeReason). */
export type JobOrderInvitationNoticeReason =
  | 'cancelled'
  | 'removed'
  | 'voided'
  | 'deleted'
  | 'reassigned'

/**
 * A server-side slice of the feed. 'everify' = the four E_VERIFY_* types, which the Work
 * Authorization settings page lists on its own; omitted = every type (the inbox).
 */
export type NotificationCategory = 'everify'

export interface NotificationScopeQuery {
  category?: NotificationCategory
}

export type UrgencyLevel = 'urgent' | 'warning' | 'upcoming' | 'expired'

export type BannerStatus = 'expired' | '7_days' | '30_days' | '60_days' | null

export interface NotificationMetadata {
  applicantId?: string
  candidateName?: string
  branch?: string
  expirationDate?: string
  reminderWindow?: number
  requestId?: string
  fileName?: string
  timeOffRequestId?: string
  timeOffType?: string
  // TEAM_ROLE_* (STFFLG-4014)
  companyId?: string
  companyName?: string
  // JOB_ORDER_INVITE_* (STFFLG-4184). Never applicantId: E-Verify recalculation deactivates every
  // notification that carries one.
  /** The ROOT job order: the order page a host-side action opens. */
  jobOrderId?: string
  /** The invitation the notification is about. */
  invitationId?: string
  orderNumber?: string
  /** The host distribution branch, pinned when the notification was written. */
  hostBranchName?: string
  /** The branch the invitee helps from, pinned when the notification was written. */
  helperBranchName?: string
  /** JOB_ORDER_INVITE_WITHDRAWN only. */
  reason?: JobOrderInvitationNoticeReason
}

export interface Notification {
  id: string
  recipientUserId: string
  type: NotificationType
  title: string
  message: string
  isRead: boolean
  isDismissed: boolean
  isActive: boolean
  metadata?: NotificationMetadata
  ctaLink?: string
  agencyId: string
  createdDate: string
  modifiedDate: string
}

export interface NotificationsResponse {
  data: Notification[]
  total: number
  page: number
  limit: number
}

export interface NotificationsQuery {
  page?: number
  limit?: number
  isRead?: boolean
  type?: NotificationType | 'all'
  search?: string
  category?: NotificationCategory
}

export interface NotificationSummaryResponse {
  total: number
  sevenDays: number
  thirtyDays: number
  sixtyDays: number
  expired: number
}

export interface UnreadCountResponse {
  count: number
}

export interface EVerifyBannerResponse {
  showBanner: boolean
  status: BannerStatus
  expirationDate: string | null
  candidateName: string
  daysUntilExpiry: number
}

export interface TaskSummary {
  total: number
  urgent: number
  warning: number
  upcoming: number
  expired: number
}

export const URGENCY_CONFIG: Record<
  UrgencyLevel,
  {label: string; emoji: string; textColor: string; bgColor: string}
> = {
  urgent: {label: 'Urgent', emoji: '🔴', textColor: '#991B1B', bgColor: '#FEE2E2'},
  warning: {label: 'Warning', emoji: '🟠', textColor: '#9A3412', bgColor: '#FFF7ED'},
  upcoming: {label: 'Upcoming', emoji: '🟡', textColor: '#92400E', bgColor: '#FFFBEB'},
  expired: {label: 'Expired', emoji: '⚫', textColor: 'var(--neutral-600)', bgColor: '#F1F3F4'},
}

export const NOTIFICATION_DOT_COLORS: Record<string, string> = {
  E_VERIFY_EXPIRATION_7_DAYS: '#E74C3C',
  E_VERIFY_EXPIRATION_30_DAYS: '#EACE60',
  E_VERIFY_EXPIRATION_60_DAYS: '#EACE60',
  E_VERIFY_EXPIRED: 'var(--neutral-600)',
  ESIGN_SIGNING_REQUEST: 'var(--brand)',
  ESIGN_REMINDER: '#F59E0B',
  ESIGN_COMPLETED: 'var(--success-500)',
  TIME_OFF_REQUEST_APPROVED: 'var(--success-500)',
  TIME_OFF_REQUEST_DENIED: 'var(--error-500)',
  TEAM_ROLE_ASSIGNED: 'var(--brand)',
  TEAM_ROLE_REMOVED: 'var(--neutral-600)',
  JOB_ORDER_INVITE_RECEIVED: 'var(--brand)',
  JOB_ORDER_INVITE_ACCEPTED: 'var(--success-500)',
  JOB_ORDER_INVITE_DECLINED: 'var(--error-500)',
  JOB_ORDER_INVITE_WITHDRAWN: 'var(--neutral-600)',
  JOB_ORDER_INVITE_EXPIRED: 'var(--warning-500)',
}

const E_VERIFY_TYPES = new Set<string>([
  'E_VERIFY_EXPIRATION_60_DAYS',
  'E_VERIFY_EXPIRATION_30_DAYS',
  'E_VERIFY_EXPIRATION_7_DAYS',
  'E_VERIFY_EXPIRED',
])

/** Returns whether a notification is a work-authorization (E-Verify) expiration reminder. */
export function isEVerifyNotification(type: string): boolean {
  return E_VERIFY_TYPES.has(type)
}

const ESIGN_TYPES = new Set<string>(['ESIGN_SIGNING_REQUEST', 'ESIGN_REMINDER', 'ESIGN_COMPLETED'])

export function isESignNotification(type: string): boolean {
  return ESIGN_TYPES.has(type)
}

const TIME_OFF_TYPES = new Set<string>(['TIME_OFF_REQUEST_APPROVED', 'TIME_OFF_REQUEST_DENIED'])

/** Returns whether a notification represents a candidate Time Off decision. */
export function isTimeOffNotification(type: string): boolean {
  return TIME_OFF_TYPES.has(type)
}

const TEAM_ROLE_TYPES = new Set<string>(['TEAM_ROLE_ASSIGNED', 'TEAM_ROLE_REMOVED'])

/** Returns whether a notification is a client order-access team change (STFFLG-4014). */
export function isTeamRoleNotification(type: string): boolean {
  return TEAM_ROLE_TYPES.has(type)
}

export const JOB_ORDER_INVITATION_NOTIFICATION_TYPES: readonly JobOrderInvitationNotificationType[] =
  [
    'JOB_ORDER_INVITE_RECEIVED',
    'JOB_ORDER_INVITE_ACCEPTED',
    'JOB_ORDER_INVITE_DECLINED',
    'JOB_ORDER_INVITE_WITHDRAWN',
    'JOB_ORDER_INVITE_EXPIRED',
  ]

const JOB_ORDER_INVITATION_TYPES = new Set<string>(JOB_ORDER_INVITATION_NOTIFICATION_TYPES)

/** Returns whether a notification is about a cross-branch job order invitation (STFFLG-4184). */
export function isJobOrderInvitationNotification(
  type: string,
): type is JobOrderInvitationNotificationType {
  return JOB_ORDER_INVITATION_TYPES.has(type)
}

/**
 * The invitee's own Pending Orders page: the action of an invitee's EXPIRED notification (and its
 * RECEIVED one). Equal to SIDEBAR_ROUTES.PENDING_SERVICE_ORDERS (asserted in its test).
 */
export const INVITEE_PENDING_ORDERS_PATH = '/manage-orders/pending'

/**
 * Which side of an invitation the recipient is on. RECEIVED and WITHDRAWN only ever go to the
 * invitee, ACCEPTED and DECLINED only to the host side. EXPIRED goes to both, and the metadata is
 * the same for each, so the side is read from the link mspplatform wrote for that recipient: the
 * invitee's is their Pending Orders, the host side's the order page. Null for any other type.
 */
export function invitationNotificationSide(notification: {
  type: string
  ctaLink?: string
}): 'invitee' | 'host' | null {
  switch (notification.type) {
    case 'JOB_ORDER_INVITE_RECEIVED':
    case 'JOB_ORDER_INVITE_WITHDRAWN':
      return 'invitee'
    case 'JOB_ORDER_INVITE_ACCEPTED':
    case 'JOB_ORDER_INVITE_DECLINED':
      return 'host'
    case 'JOB_ORDER_INVITE_EXPIRED':
      return notification.ctaLink?.startsWith(INVITEE_PENDING_ORDERS_PATH) ? 'invitee' : 'host'
    default:
      return null
  }
}

/** The source a notification came from, as the inbox's Type column names it. */
export function getNotificationTypeLabel(type: string): string {
  if (isEVerifyNotification(type)) return 'Work Authorization'
  if (isESignNotification(type)) return 'eSign'
  if (isTimeOffNotification(type)) return 'Time Off'
  if (isTeamRoleNotification(type)) return 'Order Access'
  if (isJobOrderInvitationNotification(type)) return 'Order Invitation'
  return 'Other'
}

/**
 * The call to action shared by the bell's cards and the inbox rows. An invitation's EXPIRED reads
 * by recipient, so pass the notification's `ctaLink` for it. Whether there is an action at all is
 * getNotificationDestination's answer (WITHDRAWN never has one).
 */
export function getNotificationActionLabel(type: string, ctaLink?: string): string {
  if (type === 'ESIGN_SIGNING_REQUEST' || type === 'ESIGN_REMINDER') return 'Review & Sign'
  if (type === 'ESIGN_COMPLETED') return 'View Document'
  if (isTimeOffNotification(type)) return 'View Time Off'
  if (type === 'JOB_ORDER_INVITE_RECEIVED') return 'View Invitation'
  if (type === 'JOB_ORDER_INVITE_ACCEPTED' || type === 'JOB_ORDER_INVITE_DECLINED') {
    return 'Open Order'
  }
  if (type === 'JOB_ORDER_INVITE_EXPIRED') {
    return invitationNotificationSide({type, ctaLink}) === 'invitee'
      ? 'View Pending Orders'
      : 'Open Order'
  }
  return 'Open Task'
}

/**
 * The branch a card's meta line names: `metadata.branch` where the type carries one, else, for an
 * invitation, the other side's branch (the host branch for the invitee, the helper's for the host
 * side). Empty when there is none.
 */
export function getNotificationBranch(notification: Notification): string {
  const metadata = notification.metadata
  if (metadata?.branch) return metadata.branch
  const side = invitationNotificationSide(notification)
  if (side === 'invitee') return metadata?.hostBranchName ?? ''
  if (side === 'host') return metadata?.helperBranchName ?? ''
  return ''
}

/** The candidate search an E-Verify reminder points at, or null when it names no one. */
export function getCandidateProfileLink(notification: Notification): string | null {
  if (notification.ctaLink?.startsWith('/candidates?')) return notification.ctaLink
  const candidateName = notification.metadata?.candidateName
  if (!candidateName) return null
  // Backwards compat: parse candidateName for old notifications with the legacy ctaLink format.
  // Remove once all old notifications have cycled out and backwards compatibility is no longer needed.
  const [firstName, ...rest] = candidateName.split(' ')
  const lastName = rest.join(' ')
  const params = new URLSearchParams()
  if (firstName) params.set('firstName', firstName)
  if (lastName) params.set('lastName', lastName)
  return `/candidates?${params.toString()}`
}

export function getUrgencyFromDays(days: number): UrgencyLevel {
  if (days <= 0) return 'expired'
  if (days <= 7) return 'urgent'
  if (days <= 30) return 'warning'
  return 'upcoming'
}

export function getUrgencyFromType(type: NotificationType): UrgencyLevel {
  switch (type) {
    case 'E_VERIFY_EXPIRATION_7_DAYS':
      return 'urgent'
    case 'E_VERIFY_EXPIRATION_30_DAYS':
      return 'warning'
    case 'E_VERIFY_EXPIRATION_60_DAYS':
      return 'upcoming'
    case 'E_VERIFY_EXPIRED':
      return 'expired'
    default:
      return 'expired'
  }
}

export function getReminderWindowDays(type: NotificationType): number {
  switch (type) {
    case 'E_VERIFY_EXPIRATION_60_DAYS':
      return 60
    case 'E_VERIFY_EXPIRATION_30_DAYS':
      return 30
    case 'E_VERIFY_EXPIRATION_7_DAYS':
      return 7
    case 'E_VERIFY_EXPIRED':
      return 0
    default:
      return 0
  }
}

const MS_PER_DAY = 86_400_000

export function getDaysRemaining(expirationDate?: string): number {
  if (!expirationDate) return 0
  const now = new Date()
  const exp = new Date(expirationDate)
  return Math.ceil((exp.getTime() - now.getTime()) / MS_PER_DAY)
}

export function formatTimeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / MS_PER_DAY)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} min ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  return date.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
}
